/**
 * Compare-and-set for a work area's `policies` (F-0288, GV-3 and GV-7 follow-up).
 *
 * `PATCH /work-areas/:id` replaces `policies` whole, and every writer builds its body from the
 * policies it read: Settings → Workflow, Settings → Policy, the other Settings screens and
 * `featit work-area-update`. Two of them working from the same read used to end with the second
 * write erasing the first one's key — a cap set in the web vanished when the CLI switched the
 * independent closer on a moment later, and nobody was told.
 *
 * So a writer sends the revision of the policies it read, and the server refuses a write whose
 * revision no longer matches what is stored, naming what changed and writing nothing.
 *
 * ⭐ **The revision is a fingerprint of the content, not `updatedAt` and not a stored counter.**
 *
 * - `updatedAt` moves on every column: renaming the work area would make a policy save stale for
 *   no reason, and it cannot say *what* changed.
 * - A stored counter needs a migration and every server-side writer of `policies` to bump it (the
 *   responsible-agent switch, next-up configs, admission, the README names); one that forgot would
 *   make the guard blind to its writes.
 * - A fingerprint of what is stored cannot be forgotten by any writer, and because it is taken per
 *   section it tells the losing writer which sections moved — the part that lets the CLI retry a
 *   change that still applies cleanly instead of re-reading the world.
 *
 * Platform-agnostic on purpose (`code/shared` rule): the server computes it to check a write, and
 * the web and the CLI compute it from the policies they read. No crypto: the hash guards against an
 * accidental race, not an adversary, and a 53-bit hash per section makes a collision negligible.
 */
import { policySections as policySectionWords } from './i18n/messages/en/policySections.js';

/** The prefix a revision carries, so a later format can be told apart from this one. */
const REVISION_PREFIX = 'p1:';

/** Why a work area's policies write was refused. */
export const WORK_AREA_POLICIES_CHANGED = 'work_area_policies_changed';

/** Stable prefix of the refusal's sentence, for a client that only sees the message. */
export const WORK_AREA_POLICIES_CHANGED_MESSAGE =
  "This work area's settings changed since they were read";

/** What a refused policies write hands back, besides its sentence. */
export interface IWorkAreaPoliciesConflict {
  reason: typeof WORK_AREA_POLICIES_CHANGED;
  workAreaId: string;
  /** The revision the policies have now, to compose the next attempt against. */
  currentRevision: string;
  /**
   * The sections that differ between the revision the writer read and what is stored, as dotted
   * paths (`workItems.maxInProgress`). Empty only when the revision could not be compared section
   * by section; it is never used to mean "not computed".
   */
  changedFields: string[];
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * JSON with every object's keys sorted, so the same policies give the same text whichever order
 * Postgres' `jsonb` or a client happened to write their keys in. `undefined` is dropped the way
 * `JSON.stringify` drops it, because it never reaches the database.
 */
function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((entry) => (entry === undefined ? 'null' : canonicalJson(entry))).join(',')}]`;
  }
  if (isPlainRecord(value)) {
    const entries = Object.keys(value)
      .filter((key) => value[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(value[key])}`);
    return `{${entries.join(',')}}`;
  }
  return JSON.stringify(value ?? null);
}

/** cyrb53: a fast 53-bit string hash, as fixed-width hex. */
function hash53(text: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    h1 = Math.imul(h1 ^ code, 2654435761);
    h2 = Math.imul(h2 ^ code, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(14, '0');
}

/**
 * The sections a policies object is compared by: `<key>.<subkey>` for a top-level object with keys
 * of its own (`workItems.maxInProgress`, `workItems.default`), and `<key>` for anything else.
 *
 * Two levels, because that is where the writers divide the work: the cap, the closer's default
 * policy, the template and the notes are siblings under `workItems`, and the screens that write
 * them are different screens. Deeper would make a template's `default` look like three unrelated
 * changes; shallower would make every `workItems` edit conflict with every other.
 */
export function policySections(policies: unknown): Map<string, unknown> {
  const sections = new Map<string, unknown>();
  if (!isPlainRecord(policies)) return sections;
  for (const key of Object.keys(policies)) {
    const value = policies[key];
    if (value === undefined) continue;
    const children = isPlainRecord(value)
      ? Object.keys(value).filter((child) => value[child] !== undefined)
      : [];
    if (children.length === 0) {
      sections.set(key, value);
      continue;
    }
    for (const child of children) {
      sections.set(`${key}.${child}`, (value as Record<string, unknown>)[child]);
    }
  }
  return sections;
}

function sectionHashes(policies: unknown): Map<string, string> {
  const hashes = new Map<string, string>();
  for (const [path, value] of policySections(policies))
    hashes.set(path, hash53(canonicalJson(value)));
  return hashes;
}

/**
 * The revision of a work area's policies: what a writer sends back as `expectedPoliciesRevision`.
 * Opaque to every caller but this module; two equal policies always give the same revision.
 */
export function policiesRevisionOf(policies: unknown): string {
  const entries = [...sectionHashes(policies)]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([path, digest]) => `${encodeURIComponent(path)}=${digest}`);
  return `${REVISION_PREFIX}${entries.join(',')}`;
}

function parseRevision(revision: string): Map<string, string> | null {
  if (!revision.startsWith(REVISION_PREFIX)) return null;
  const body = revision.slice(REVISION_PREFIX.length);
  const hashes = new Map<string, string>();
  if (body === '') return hashes;
  for (const entry of body.split(',')) {
    const separator = entry.lastIndexOf('=');
    if (separator <= 0) return null;
    let path: string;
    try {
      path = decodeURIComponent(entry.slice(0, separator));
    } catch {
      return null;
    }
    const digest = entry.slice(separator + 1);
    if (!/^[0-9a-f]{14}$/.test(digest)) return null;
    hashes.set(path, digest);
  }
  return hashes;
}

/** Whether a string is a revision this module issued, so a malformed one is refused as such. */
export function isPoliciesRevision(value: unknown): value is string {
  return typeof value === 'string' && parseRevision(value) !== null;
}

function differingSections(before: Map<string, string>, after: Map<string, string>): string[] {
  const changed = new Set<string>();
  for (const [path, digest] of before) if (after.get(path) !== digest) changed.add(path);
  for (const path of after.keys()) if (!before.has(path)) changed.add(path);
  return [...changed].sort();
}

/**
 * The sections that changed between the revision a writer read and the policies stored now. An
 * empty list means the write may go ahead; `null` means the revision is not one this module issued.
 */
export function policySectionsChangedSince(
  expectedRevision: string,
  current: unknown,
): string[] | null {
  const expected = parseRevision(expectedRevision);
  if (!expected) return null;
  return differingSections(expected, sectionHashes(current));
}

/**
 * The sections a writer's own change touches: the difference between the policies it read and the
 * policies it is about to send. The CLI compares these with a refusal's `changedFields` to decide
 * whether its change still applies cleanly on top of somebody else's.
 */
export function changedPolicySections(before: unknown, after: unknown): string[] {
  return differingSections(sectionHashes(before), sectionHashes(after));
}

/**
 * Sections a writer may leave out of a whole `policies` write without dropping anything: the server
 * derives or keeps them itself (`workAreaPoliciesAsStored`, ADR-128), so an omission there is not a
 * decision.
 */
const SERVER_KEPT_POLICY_SECTIONS = new Set(['workItems.rulesDescription', 'workItems.rulesNotes']);

function holdsSomething(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (isPlainRecord(value)) return Object.keys(value).length > 0;
  return true;
}

/**
 * The sections (`policySections`) a whole `policies` write would drop: stored with something in
 * them, and absent from what the writer sent (I-0812, ADR-128 decision 5).
 *
 * A section left out of a whole write is one the writer did not name, and dropping it is the silent
 * change the compare-and-set and `policiesPatch` exist to prevent. A writer that means to remove one
 * names it: `null` in a `policiesPatch`, or `null` in the whole object. A change inside a section
 * the writer did send is that writer's statement of the section, and is not a drop.
 */
export function droppedPolicySections(stored: unknown, sent: unknown): string[] {
  const kept = policySections(sent);
  const topLevel = isPlainRecord(sent) ? sent : {};
  const dropped: string[] = [];
  for (const [path, value] of policySections(stored)) {
    if (SERVER_KEPT_POLICY_SECTIONS.has(path) || !holdsSomething(value)) continue;
    if (kept.has(path)) continue;
    // An explicit `null` for the section, or for the object it sits in, names it.
    const [top, child] = path.split('.');
    const parent = topLevel[top];
    if (parent === null) continue;
    if (child !== undefined && isPlainRecord(parent) && parent[child] === null) continue;
    dropped.push(path);
  }
  return dropped.sort();
}

/**
 * The refusal's sentence. It names the sections that moved and says plainly that nothing was
 * written, so a writer that cannot see the payload still knows what to re-read.
 */
export function workAreaPoliciesChangedMessage(changedFields: string[]): string {
  const changed =
    changedFields.length > 0 ? ` Changed since then: ${changedFields.join(', ')}.` : '';
  return (
    `${WORK_AREA_POLICIES_CHANGED_MESSAGE}, so nothing was saved.${changed} ` +
    'Read them again and repeat the change against what is stored now.'
  );
}

/** Classify a refusal from its payload, never from its message. `null` for anything else. */
export function workAreaPoliciesConflictOf(payload: unknown): IWorkAreaPoliciesConflict | null {
  if (!isPlainRecord(payload)) return null;
  if (payload.reason !== WORK_AREA_POLICIES_CHANGED) return null;
  if (typeof payload.workAreaId !== 'string') return null;
  if (typeof payload.currentRevision !== 'string') return null;
  const changedFields = payload.changedFields;
  if (!Array.isArray(changedFields) || changedFields.some((field) => typeof field !== 'string')) {
    return null;
  }
  return {
    reason: WORK_AREA_POLICIES_CHANGED,
    workAreaId: payload.workAreaId,
    currentRevision: payload.currentRevision,
    changedFields: [...(changedFields as string[])],
  };
}

/**
 * What a section is called on a screen. The dotted path is right for an agent and for the log; a
 * person reading "workItems.maxInProgress" should read "the work-in-progress limit".
 *
 * ⛔ **No path reaches a screen (I-1312).** A section is named by its exact path, else by the
 * section it sits in, else as {@link OTHER_POLICY_SECTION}. A person once read "Someone else changed
 * externalAccess.trustedSources": the table did not know External access, and the path was shown
 * "rather than not at all". Every section the code stores has a line here
 * (`web/src/features/settings/policySectionLabels.guard.test.ts`).
 *
 * Built in calls marked pure (I-1971, I-2453): a bundle that imports this module and never names a
 * section leaves the table and its words out. The web's launch entry imports it through `ProjectStore`;
 * unmarked, the build kept the calls and all of `policySections` with them, about 2 KB of the entry's
 * budget for words no first screen draws.
 */
export const POLICY_SECTION_LABELS: Readonly<Record<string, string>> =
  /* @__PURE__ */ Object.freeze(
    /* @__PURE__ */ Object.fromEntries(
      // A section's path writes its dot `_` in the catalogue (`workItems_maxInProgress`).
      /* @__PURE__ */ Object.entries(policySectionWords.sections).map(([id, label]) => [
        id.replace('_', '.'),
        label,
      ]),
    ),
  );

/** What a section nobody has named yet is called: plain words, never its path. */
export const OTHER_POLICY_SECTION: string = policySectionWords.other;

/** The table's own line for a path; never a property every object has (`constructor`). */
function policySectionLabel(path: string): string | undefined {
  return Object.prototype.hasOwnProperty.call(POLICY_SECTION_LABELS, path)
    ? POLICY_SECTION_LABELS[path]
    : undefined;
}

export function describePolicySection(path: string): string {
  return policySectionLabel(path) ?? policySectionLabel(path.split('.')[0]) ?? OTHER_POLICY_SECTION;
}

/**
 * I-1792 (F-0596 RFE-25): the same compare-and-set for the work area's own details, which are
 * columns, not policies. The writer sends the values it read as `expectedDetails`; a save whose
 * values are no longer the stored ones is refused with 409 and nothing is written.
 */
export const WORK_AREA_DETAILS_CHANGED = 'work_area_details_changed';

/** The details a precondition may name, each as the form on Work area → General holds it. */
export const WORK_AREA_DETAIL_KEYS = [
  'name',
  'slug',
  'description',
  'parentId',
  'visibility',
  'workAreaTypeId',
  'linkDocumentReferences',
  // Other settings writes with no policies revision of their own (I-1792): the default category of
  // Email intake, and whether an agent is assignable here (Settings → Agents).
  'defaultCategory',
  'agentActivation',
] as const;

export type WorkAreaDetailKeyType = (typeof WORK_AREA_DETAIL_KEYS)[number];

/** What a detail is called on a screen. */
export const WORK_AREA_DETAIL_LABELS: Readonly<Record<WorkAreaDetailKeyType, string>> =
  Object.freeze({
    name: 'the title',
    slug: 'the slug',
    description: 'the description',
    parentId: 'the parent',
    visibility: 'the visibility',
    workAreaTypeId: 'the type',
    linkDocumentReferences: 'the document references',
    defaultCategory: 'the default category',
    agentActivation: 'which agents are assignable here',
  });

/** What a refused details write hands back, besides its sentence. */
export interface IWorkAreaDetailsConflict {
  reason: typeof WORK_AREA_DETAILS_CHANGED;
  workAreaId: string;
  changedFields: WorkAreaDetailKeyType[];
}

/** A stored or sent detail, the way the form holds it: nothing is `''`, a switch is a boolean. */
function detailValue(key: WorkAreaDetailKeyType, value: unknown): unknown {
  if (key === 'linkDocumentReferences' || key === 'agentActivation') return value === true;
  return value === null || value === undefined ? '' : value;
}

/**
 * The named details whose stored value is not the one the writer read. Only keys the writer sent
 * are compared, so a writer that read nothing about a detail cannot be refused over it.
 */
export function changedWorkAreaDetails(
  expected: Readonly<Record<string, unknown>>,
  stored: Readonly<Record<string, unknown>>,
): WorkAreaDetailKeyType[] {
  return WORK_AREA_DETAIL_KEYS.filter(
    (key) =>
      Object.prototype.hasOwnProperty.call(expected, key) &&
      detailValue(key, expected[key]) !== detailValue(key, stored[key]),
  );
}

/** The refusal's sentence: what moved, and that nothing was written. */
export function workAreaDetailsChangedMessage(
  changedFields: readonly WorkAreaDetailKeyType[],
): string {
  const names = changedFields.map((key) => WORK_AREA_DETAIL_LABELS[key]);
  const listed =
    names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
  return (
    `Someone else changed ${listed || 'these details'} while you were editing, so nothing was saved. ` +
    'Read the work area again and repeat the change against what is stored now.'
  );
}

/** Classify a refusal from its payload, never from its message. `null` for anything else. */
export function workAreaDetailsConflictOf(payload: unknown): IWorkAreaDetailsConflict | null {
  if (!isPlainRecord(payload) || payload.reason !== WORK_AREA_DETAILS_CHANGED) return null;
  if (typeof payload.workAreaId !== 'string' || !Array.isArray(payload.changedFields)) return null;
  const known = new Set<string>(WORK_AREA_DETAIL_KEYS);
  if (payload.changedFields.some((field) => typeof field !== 'string' || !known.has(field))) {
    return null;
  }
  return {
    reason: WORK_AREA_DETAILS_CHANGED,
    workAreaId: payload.workAreaId,
    changedFields: [...(payload.changedFields as WorkAreaDetailKeyType[])],
  };
}
